<template>
  <div class="stats-container">
    <!-- 标签切换栏 -->
    <div class="tab-nav">
      <button 
        v-for="tab in tabs" 
        :key="tab.key"
        class="tab-btn"
        :class="[activeTab === tab.key ? 'tab-btn-active' : '']"
        @click="switchTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- NPC交易统计 -->
    <div v-if="activeTab === 'npcStats'" class="stat-card">
      <div class="card-header">
        <h2 class="card-title">NPC交易统计</h2>
        <button 
          class="refresh-btn"
          @click="fetchNpcStats"
          :disabled="npcLoading"
        >
          <i class="fa fa-refresh" :class="{ 'refresh-spin': npcLoading }"></i>
        </button>
      </div>
      
      <div class="card-body">
        <div v-if="npcLoading && npcStats.length === 0" class="empty-state">
          <div class="empty-content">
            <i class="fa fa-spinner fa-spin mr-2"></i>
            <span>加载中...</span>
          </div>
        </div>
        <div v-else-if="npcStats.length === 0 && !npcLoading" class="empty-state">
          <div class="empty-content">
            <i class="fa fa-info-circle mr-2"></i>
            <span>暂无NPC交易数据</span>
          </div>
        </div>

        <!-- 按名称分组渲染 -->
        <div 
          v-for="group in groupedNpcStats" 
          :key="group.name"
          class="stat-group fade-in"
        >
          <!-- 分组标题（可点击展开/折叠） -->
          <div 
            class="stat-header group-header"
            @click="toggleGroup(group.name)"
          >
            <div class="stat-label">
              <div 
                class="stat-icon"
                :class="getNpcColorClass(group.name).bg"
              >
                <i class="fa fa-user-o text-xs" :class="getNpcColorClass(group.name).text"></i>
              </div>
              <span>{{ group.name }}</span>
              <span class="group-count">({{ group.items.length }} 条)</span>
              <i 
                class="fa fa-chevron-down group-toggle"
                :class="{ 'rotate-180': expandedGroups.has(group.name) }"
              ></i>
            </div>
            <span class="stat-value">{{ (group.totalCount || 0).toLocaleString() }}</span>
          </div>

          <!-- 分组进度条 -->
          <div class="progress-bar">
            <div 
              class="progress-fill"
              :class="getNpcColorClass(group.name).bar"
              :style="{ width: `${getGroupPercentage(group)}%` }"
            ></div>
          </div>

          <!-- 展开的明细项 -->
          <div 
            v-if="expandedGroups.has(group.name)"
            class="group-items"
          >
            <div 
              v-for="item in group.items" 
              :key="item.name"
              class="stat-item child-item"
            >
              <div class="stat-header">
                <div class="stat-label">
                  <span class="child-name">{{ item.name }}</span>
                </div>
                <span class="stat-value">{{ (item.count || 0).toLocaleString() }}</span>
              </div>
              <div class="progress-bar child-progress">
                <div 
                  class="progress-fill"
                  :class="getNpcColorClass(group.name).bar"
                  :style="{ width: `${getPercentage(item.count)}%` }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 物品掉落统计 -->
    <div v-if="activeTab === 'dropStats'" class="stat-card">
      <div class="card-header">
        <h2 class="card-title">物品掉落统计</h2>
        <button 
          class="refresh-btn"
          @click="fetchDropStats"
          :disabled="dropLoading"
        >
          <i class="fa fa-refresh" :class="{ 'refresh-spin': dropLoading }"></i>
        </button>
      </div>
      
      <div class="card-body">
        <div v-if="dropLoading && dropStats.length === 0" class="empty-state">
          <div class="empty-content">
            <i class="fa fa-spinner fa-spin mr-2"></i>
            <span>加载中...</span>
          </div>
        </div>
        <div v-else-if="dropStats.length === 0 && !dropLoading" class="empty-state">
          <div class="empty-content">
            <i class="fa fa-info-circle mr-2"></i>
            <span>暂无物品掉落数据</span>
          </div>
        </div>

        <!-- 按名称分组渲染 -->
        <div 
          v-for="group in groupedDropStats" 
          :key="group.name"
          class="stat-group fade-in"
        >
          <!-- 分组标题（可点击展开/折叠） -->
          <div 
            class="stat-header group-header"
            @click="toggleGroup(group.name)"
          >
            <div class="stat-label">
              <div class="stat-icon bg-primary text-white">
                <i class="fa fa-gem text-xs"></i>
              </div>
              <span>{{ group.name }}</span>
              <span class="group-count">({{ group.items.length }} 条)</span>
              <i 
                class="fa fa-chevron-down group-toggle"
                :class="{ 'rotate-180': expandedGroups.has(group.name) }"
              ></i>
            </div>
            <span class="stat-value">{{ (group.totalCount || 0).toLocaleString() }}</span>
          </div>

          <!-- 分组进度条 -->
          <div class="progress-bar">
            <div 
              class="progress-fill bg-primary"
              :style="{ width: `${getDropGroupPercentage(group)}%` }"
            ></div>
          </div>

          <!-- 展开的明细项 -->
          <div 
            v-if="expandedGroups.has(group.name)"
            class="group-items"
          >
            <div 
              v-for="item in group.items" 
              :key="item.name"
              class="stat-item child-item"
            >
              <div class="stat-header">
                <div class="stat-label">
                  <span class="child-name">{{ item.name }}</span>
                </div>
                <span class="stat-value">{{ (item.count || 0).toLocaleString() }}</span>
              </div>
              <div class="progress-bar child-progress">
                <div 
                  class="progress-fill bg-primary"
                  :style="{ width: `${getDropPercentage(item.count)}%` }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 充值记录查询（移除区服选择器） -->
    <div v-if="activeTab === 'chargeRecords'" class="stat-card">
      <div class="card-header">
        <h2 class="card-title">游戏充值记录查询</h2>
        <div class="search-bar">
          <!-- 仅保留搜索框，移除区服选择 -->
          <div class="search-input-wrapper">
            <input 
              type="text" 
              v-model="chargeSearchKey"
              @keyup.enter="loadChargeRecords"
              placeholder="搜索用户名，按Enter查询..." 
              class="form-input"
            >
            <i class="fa fa-search search-icon"></i>
          </div>
        </div>
      </div>

      <!-- 充值记录表格 -->
      <div class="card-body">
        <div class="table-wrapper scrollbar-hide">
          <table class="data-table">
            <thead>
              <tr>
                <th class="table-header">用户名</th>
                <th class="table-header">充值金额(元)</th>
                <th class="table-header">充值时间</th>
                <th class="table-header">充值介质</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="chargeLoading">
                <td colspan="4" class="empty-state">
                  <div class="empty-content">
                    <i class="fa fa-spinner fa-spin mr-2"></i>
                    <span>加载中...</span>
                  </div>
                </td>
              </tr>
              <tr v-else-if="chargeRecords.length === 0">
                <td colspan="4" class="empty-state">
                  <div class="empty-content">
                    <i class="fa fa-search mr-2"></i>
                    <span>未找到充值记录</span>
                  </div>
                </td>
              </tr>
              <tr 
                v-for="record in chargeRecords" 
                :key="record.id || record.username + record.rechargeTime"
                class="table-row"
              >
                <td class="table-cell">{{ record.username || '未知用户' }}</td>
                <td class="table-cell text-primary font-medium">{{ (record.amount || 0).toFixed(2) }}</td>
                <td class="table-cell text-gray-400">{{ record.rechargeTime || '未知时间' }}</td>
                <td class="table-cell text-gray-400">{{ record.description || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页控件 -->
        <div class="pagination-bar" v-if="chargeTotalCount > 0">
          <p class="pagination-info">显示 {{ chargeStartIndex }} 至 {{ chargeEndIndex }} 条，共 {{ chargeTotalCount }} 条</p>
          <div class="pagination-buttons">
            <button 
              class="page-btn" 
              :disabled="chargeCurrentPage === 1"
              @click="jumpToChargePage(1)"
            >
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button 
              class="page-btn" 
              :disabled="chargeCurrentPage === 1"
              @click="jumpToChargePage(chargeCurrentPage - 1)"
            >
              <i class="fa fa-angle-left"></i>
            </button>
            <button class="page-btn active">{{ chargeCurrentPage }}</button>
            <button 
              class="page-btn" 
              :disabled="chargeCurrentPage === chargeTotalPages"
              @click="jumpToChargePage(chargeCurrentPage + 1)"
            >
              <i class="fa fa-angle-right"></i>
            </button>
            <button 
              class="page-btn" 
              :disabled="chargeCurrentPage === chargeTotalPages"
              @click="jumpToChargePage(chargeTotalPages)"
            >
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { LOG_API } from '@/utils/apiBase';
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import axios from 'axios';

// 接收父组件传入的参数（新增selectedDate）
const props = defineProps({
  serverId: {
    type: String,
    required: true
  },
  selectedDate: { // 新增：接收父组件的选中日期
    type: String,
    required: false,
    default: ''
  }
});

// 标签配置
const tabs = ref([
  { key: 'npcStats', label: 'NPC交易统计' },
  { key: 'dropStats', label: '物品掉落统计' },
  { key: 'chargeRecords', label: '充值记录查询' }
]);
const activeTab = ref('npcStats');

// NPC统计相关
const npcStats = ref([]);
const npcLoading = ref(false);

// 掉落统计相关
const dropStats = ref([]);
const dropLoading = ref(false);

// 充值记录相关（移除本地区服变量）
const chargeRecords = ref([]);
const chargeLoading = ref(false);
const chargeCurrentPage = ref(1);
const chargePageSize = ref(10);
const chargeTotalCount = ref(0);
const chargeSearchKey = ref('');
const chargeTotalPages = computed(() => Math.ceil(chargeTotalCount.value / chargePageSize.value));

// 新增：展开/折叠状态管理
const expandedGroups = ref(new Set());

// 充值分页计算属性
const chargeStartIndex = computed(() => {
  if (chargeTotalCount.value === 0) return 0;
  return (chargeCurrentPage.value - 1) * chargePageSize.value + 1;
});
const chargeEndIndex = computed(() => {
  const end = chargeCurrentPage.value * chargePageSize.value;
  return end > chargeTotalCount.value ? chargeTotalCount.value : end;
});

// 自动刷新定时器
let refreshInterval = null;

// 方法：切换标签
const switchTab = (tabKey) => {
  activeTab.value = tabKey;
  // 切换到对应标签时自动加载数据
  if (tabKey === 'npcStats' && npcStats.value.length === 0) fetchNpcStats();
  if (tabKey === 'dropStats' && dropStats.value.length === 0) fetchDropStats();
  if (tabKey === 'chargeRecords' && chargeRecords.value.length === 0) loadChargeRecords();
};

// 方法：获取NPC颜色样式
const getNpcColorClass = (name) => {
  // 简单的哈希取色，可根据实际需求扩展
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const colors = [
    { bg: 'bg-primary', text: 'text-white', bar: 'bg-primary' },
    { bg: 'bg-secondary', text: 'text-white', bar: 'bg-secondary' },
    { bg: 'bg-success', text: 'text-white', bar: 'bg-success' },
    { bg: 'bg-warning', text: 'text-white', bar: 'bg-warning' },
    { bg: 'bg-danger', text: 'text-white', bar: 'bg-danger' }
  ];
  return colors[hash % colors.length];
};

// 方法：计算NPC进度条百分比（明细项）
const getPercentage = (count) => {
  if (npcStats.value.length === 0) return 0;
  const maxValue = Math.max(...npcStats.value.map(stat => stat.count || 0));
  return maxValue > 0 ? Math.round((count / maxValue) * 100) : 0;
};

// 方法：计算掉落进度条百分比（明细项）
const getDropPercentage = (count) => {
  if (dropStats.value.length === 0) return 0;
  const maxValue = Math.max(...dropStats.value.map(stat => stat.count || 0));
  return maxValue > 0 ? Math.round((count / maxValue) * 100) : 0;
};

// 新增：切换分组展开/折叠
const toggleGroup = (groupKey) => {
  if (expandedGroups.value.has(groupKey)) {
    expandedGroups.value.delete(groupKey);
  } else {
    expandedGroups.value.add(groupKey);
  }
};

// 新增：按名称聚合NPC数据
const groupedNpcStats = computed(() => {
  const groups = {};
  npcStats.value.forEach(item => {
    // 提取基础名称（去掉序号，如 "npc卖出的黄龙弓:10318" -> "npc卖出的黄龙弓"）
    const baseName = item.name.replace(/:\d+$/, '');
    if (!groups[baseName]) {
      groups[baseName] = {
        name: baseName,
        totalCount: 0,
        items: []
      };
    }
    groups[baseName].totalCount += item.count || 0;
    groups[baseName].items.push(item);
  });
  return Object.values(groups);
});

// 新增：按名称聚合掉落数据
const groupedDropStats = computed(() => {
  const groups = {};
  dropStats.value.forEach(item => {
    const baseName = item.name.replace(/:\d+$/, '');
    if (!groups[baseName]) {
      groups[baseName] = {
        name: baseName,
        totalCount: 0,
        items: []
      };
    }
    groups[baseName].totalCount += item.count || 0;
    groups[baseName].items.push(item);
  });
  return Object.values(groups);
});

// 新增：计算分组进度条百分比（NPC）
const getGroupPercentage = (group) => {
  if (groupedNpcStats.value.length === 0) return 0;
  const maxValue = Math.max(...groupedNpcStats.value.map(g => g.totalCount || 0));
  return maxValue > 0 ? Math.round((group.totalCount / maxValue) * 100) : 0;
};

// 新增：计算分组进度条百分比（掉落）
const getDropGroupPercentage = (group) => {
  if (groupedDropStats.value.length === 0) return 0;
  const maxValue = Math.max(...groupedDropStats.value.map(g => g.totalCount || 0));
  return maxValue > 0 ? Math.round((group.totalCount / maxValue) * 100) : 0;
};

// 方法：获取NPC统计数据（修复selectedDate引用）
const fetchNpcStats = async () => {
  npcLoading.value = true;
  try {
    const response = await axios.get(`${LOG_API}/api/npcData`, {
      params: { 
        dbname: props.serverId,
        time: props.selectedDate ? props.selectedDate : '所有'// 替换为props.selectedDate
      }
    });
    console.log('接口：npcData ',props.serverId,props.selectedDate ? props.selectedDate : '所有');
    npcStats.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('获取NPC数据失败:', error);
    npcStats.value = [];
  } finally {
    npcLoading.value = false;
  }
};

// 方法：获取掉落统计数据（修复selectedDate引用）
const fetchDropStats = async () => {
  dropLoading.value = true;
  try {
    const response = await axios.get(`${LOG_API}/api/npcData/DropData`, {
      params: {
        dbname: props.serverId,
        time: props.selectedDate ? props.selectedDate : '所有'// 替换为props.selectedDate
      },
      timeout: 5000 // 增加5秒超时保护
    });
    console.log('接口：DropData ',props.serverId,props.selectedDate ? props.selectedDate : '所有')
    // 严格校验数据格式，适配后台仅返回name和count的字段
    if (Array.isArray(response.data)) {
      // 过滤无效数据+类型校验，避免渲染报错
      dropStats.value = response.data.filter(item => {
        return item.name !== undefined && item.count !== undefined;
      }).map(item => {
        return {
          name: item.name || '未知物品', // 空名称兜底
          count: Number(item.count) || 0 // 确保count为数字类型
        };
      });
    } else {
      dropStats.value = [];
    }
  } catch (error) {
    console.error('获取掉落数据失败:', error);
    dropStats.value = [];
  } finally {
    dropLoading.value = false;
  }
};

// 方法：加载充值记录（修复activeTimeRange为props.selectedDate）
const loadChargeRecords = async () => {
  if (chargeLoading.value) return;
  chargeLoading.value = true;
  try {
    const response = await axios.get(`${LOG_API}/api/Recharge/GetRechargeRecords`, {
      params: {
        serverId: props.serverId,
        keyword: chargeSearchKey.value.trim(),
        page: chargeCurrentPage.value,
        pageSize: chargePageSize.value,
        time: props.selectedDate ? props.selectedDate : '所有' // 替换activeTimeRange为props.selectedDate
      }
    });
    
    console.log('接口：GetRechargeRecords ',props.serverId,chargeSearchKey.value.trim(),chargeCurrentPage.value,chargePageSize.value,props.selectedDate ? props.selectedDate : '所有')
    // 适配后台返回的嵌套数据结构
    const responseData = response.data || {};
    // 从外层data中提取records数组和length总条数
    const { records = [], length = 0 } = responseData.data || {};
    
    // 字段映射：后台字段 -> 前端渲染字段
    chargeRecords.value = records.map(item => ({
      username: item.userName || '未知用户',       // 后台userName -> 前端username
      amount: Number(item.rechargeAmount) || 0,    // 后台rechargeAmount -> 前端amount（转数字）
      rechargeTime: item.rechargeTime || '未知时间',// 后台rechargeTime -> 前端rechargeTime
      description: item.rechargeDesc || '-'        // 后台rechargeDesc -> 前端description
    }));
    
    // 总条数对应后台返回的length字段
    chargeTotalCount.value = length;
  } catch (error) {
    console.error('获取充值记录失败:', error);
    chargeRecords.value = [];
    chargeTotalCount.value = 0;
  } finally {
    chargeLoading.value = false;
  }
};

// 方法：充值分页跳转
const jumpToChargePage = (page) => {
  if (page < 1 || page > chargeTotalPages.value) return;
  chargeCurrentPage.value = page;
  loadChargeRecords();
};

// 启动自动刷新
const startAutoRefresh = () => {
  refreshInterval = setInterval(() => {
    if (activeTab.value === 'npcStats') fetchNpcStats();
    if (activeTab.value === 'dropStats') fetchDropStats();
  }, 1000000); // 10秒刷新一次
};

// 监听区服和日期变化，自动刷新所有数据
watch([() => props.serverId, () => props.selectedDate], () => {
  fetchNpcStats();
  fetchDropStats();
  loadChargeRecords();
});

// 挂载时初始化
onMounted(() => {
  fetchNpcStats(); // 加载默认标签数据
  startAutoRefresh(); // 启动自动刷新
});

// 卸载时清除定时器
onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});
</script>

<style scoped>
/* 全局容器样式 - 和背包组件统一 */
.stats-container {
  background-color: #1a1a2e;
  border-radius: 4px;
  padding: 12px;
  color: #fff;
  font-size: 14px;
}

/* 标签导航栏 */
.tab-nav {
  display: flex;
  border-bottom: 1px solid #3d3d5c;
  margin-bottom: 16px;
}
.tab-btn {
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}
.tab-btn-active {
  color: #60a5fa;
  border-bottom: 2px solid #60a5fa;
}
.tab-btn:hover:not(.tab-btn-active) {
  color: #ccc;
}

/* 卡片通用样式 */
.stat-card {
  background-color: #1e293b;
  border-radius: 4px;
  overflow: hidden;
}

/* 卡片头部 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #3d3d5c;
}
.card-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0;
  color: #e0e0e0;
}
.refresh-btn {
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}
.refresh-btn:hover {
  background-color: #2d2d44;
}
.refresh-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.refresh-spin {
  animation: spin 1.5s linear infinite;
}

/* 卡片内容区 */
.card-body {
  padding: 16px;
}

/* 统计项样式 */
.stat-item {
  margin-bottom: 16px;
}
.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.stat-label {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stat-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-value {
  font-weight: bold;
  color: #fff;
}

/* 进度条样式 */
.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #2d2d44;
  border-radius: 4px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  transition: width 1s ease-in-out;
}

/* 空状态样式 */
.empty-state {
  padding: 40px 0;
  text-align: center;
  color: #888;
}
.empty-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 表单控件样式 */
.search-bar {
  display: flex;
  gap: 12px;
  align-items: center;
}
.form-select {
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  padding: 4px 8px;
  color: #fff;
  outline: none;
  font-size: 14px;
  min-width: 120px;
}
.search-input-wrapper {
  position: relative;
}
.form-input {
  width: 180px;
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  padding: 4px 8px;
  color: #fff;
  outline: none;
  font-size: 14px;
}
.search-icon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 12px;
}

/* 表格样式 */
.table-wrapper {
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.table-header {
  background-color: #2d2d44;
  padding: 8px 12px;
  text-align: left;
  font-weight: normal;
  border-bottom: 1px solid #3d3d5c;
  color: #ccc;
}
.table-row {
  transition: background-color 0.2s ease;
}
.table-row:hover {
  background-color: #2a3852;
}
.table-cell {
  padding: 8px 12px;
  border-bottom: 1px solid #3d3d5c;
}

/* 分页样式 */
.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #888;
}
.pagination-info {
  margin: 0;
}
.pagination-buttons {
  display: flex;
  gap: 4px;
}
.page-btn {
  background-color: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-btn.active {
  background-color: #3a7bd5;
  border-color: #3a7bd5;
}
.page-btn:hover:not(.active):not(:disabled) {
  background-color: #3d3d5c;
}

/* 通用样式 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}
.text-primary {
  color: #60a5fa;
}
.text-gray-400 {
  color: #888;
}
.font-medium {
  font-weight: 500;
}
.bg-primary {
  background-color: #60a5fa;
}
.bg-secondary {
  background-color: #9f7aea;
}
.bg-success {
  background-color: #38b2ac;
}
.bg-warning {
  background-color: #ed8936;
}
.bg-danger {
  background-color: #e53e3e;
}
.text-white {
  color: #ffffff;
}

/* 新增：分组相关样式 */
.stat-group {
  margin-bottom: 16px;
}
.group-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
  padding: 8px;
  border-radius: 4px;
}
.group-header:hover {
  background-color: #2a3852;
}
.group-count {
  font-size: 12px;
  color: #888;
  margin-left: 8px;
}
.group-toggle {
  margin-left: 8px;
  transition: transform 0.2s ease;
}
.group-toggle.rotate-180 {
  transform: rotate(180deg);
}
.group-items {
  margin-top: 8px;
  padding-left: 32px;
  border-left: 2px solid #3d3d5c;
}
.child-item {
  margin-bottom: 8px;
}
.child-name {
  font-size: 13px;
  color: #ccc;
}
.child-progress {
  height: 4px;
}

/* 动画 */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>